import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Warning, ArrowSquareOut, Clock, Lightning } from '@phosphor-icons/react';

const STATUS_STYLE = {
  pending: 'border-amber-300 bg-amber-50/60',
  executed: 'border-emerald-300 bg-emerald-50/60',
  failed: 'border-red-300 bg-red-50/60',
  cancelled: 'border-slate-200 bg-slate-50 opacity-75',
  expired: 'border-slate-200 bg-slate-50 opacity-75',
};

const STATUS_LABEL = {
  executed: { icon: CheckCircle, text: 'Done', cls: 'text-emerald-700' },
  failed: { icon: XCircle, text: 'Failed', cls: 'text-red-700' },
  cancelled: { icon: XCircle, text: 'Cancelled', cls: 'text-slate-500' },
  expired: { icon: Clock, text: 'Expired', cls: 'text-slate-500' },
};

export default function ActionCard({ action, onConfirm, onCancel }) {
  const { tenantSlug } = useParams();
  const tenantPath = tenantSlug ? `/t/${tenantSlug}` : '';
  const [busy, setBusy] = useState(false);
  const st = action.status || 'pending';
  const done = STATUS_LABEL[st];
  const link = action.result?.link || action.link;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(action.id); } finally { setBusy(false); }
  };

  return (
    <div className={`mt-2 rounded-xl border p-3 text-xs transition-colors ${STATUS_STYLE[st] || STATUS_STYLE.pending}`} data-testid={`copilot-action-card-${action.type}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-semibold text-slate-800 text-[13px]">
          <Lightning size={13} weight="fill" className="text-amber-500" />
          {action.title}
        </div>
        {done ? (
          <span className={`inline-flex items-center gap-1 font-semibold ${done.cls}`} data-testid="copilot-action-status">
            <done.icon size={13} weight="fill" /> {done.text}
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold" data-testid="copilot-action-status">Needs confirmation</span>
        )}
      </div>

      <dl className="space-y-1">
        {(action.fields || []).map((f, i) => (
          <div key={i} className="flex gap-2">
            <dt className="w-28 shrink-0 text-slate-500 truncate" title={f[0]}>{f[0]}</dt>
            <dd className="flex-1 text-slate-800 break-words">{f.slice(1).join(' · ')}</dd>
          </div>
        ))}
      </dl>

      {(action.warnings || []).map((w, i) => (
        <div key={i} className="mt-2 flex items-start gap-1.5 text-amber-800 bg-amber-100/70 rounded-md px-2 py-1.5" data-testid="copilot-action-warning">
          <Warning size={12} weight="fill" className="mt-0.5 shrink-0" /> {w}
        </div>
      ))}

      {action.result?.summary && st !== 'pending' && (
        <div className={`mt-2 ${st === 'failed' ? 'text-red-700' : 'text-slate-700'}`} data-testid="copilot-action-result">{action.result.summary}</div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {st === 'pending' && (
          <>
            <button
              onClick={() => run(onConfirm)}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-500 disabled:opacity-60 transition-colors"
              data-testid="copilot-action-confirm"
            >
              {busy ? 'Working…' : 'Confirm'}
            </button>
            <button
              onClick={() => run(onCancel)}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-white disabled:opacity-60 transition-colors"
              data-testid="copilot-action-cancel"
            >
              Cancel
            </button>
          </>
        )}
        {link && (st === 'executed' || st === 'pending') && (
          <Link to={`${tenantPath}${link}`} className="ml-auto inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline" data-testid="copilot-action-link">
            Open <ArrowSquareOut size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}
